var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useQuery } from '@apollo/client/react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { DialogHeader, DialogContent, useToast, Box, Typography, Alert, DialogFooter } from '@pocketlaw/tetris';
import { Falcon } from '@pocketlaw/tetris/macro';
import { boolean, object, string } from 'yup';
import { DocumentType } from 'shared/domains/apollo/generated/types';
import { CancelButton, FormDialog, SubmitButton, TextField } from 'shared/domains/forms';
import { ErrorMessage } from './ErrorMessage';
import { ShareDocumentDialogDocument } from './ShareDocumentDialog.gql';
import { useCreateExternalSharingInvite } from './useCreateExternalSharingInvite';
export function ShareDocumentDialog(props) {
    var _a;
    const { documentId, onClose, onSubmit } = props;
    const schema = object().shape({
        email: string()
            .email(t `This must be a valid email.`)
            .required(t `This is a required field.`),
        forceConvertDocument: boolean(),
        convertDocument: boolean().when('forceConvertDocument', {
            is: (forceConvertDocument) => forceConvertDocument,
            then: boolean().oneOf([true]),
            otherwise: boolean().oneOf([false]),
        }),
    });
    const { data, error: queryError, loading, } = useQuery(ShareDocumentDialogDocument, {
        variables: { id: documentId },
    });
    const { createInvite, error } = useCreateExternalSharingInvite();
    const { addToast } = useToast();
    const isComposed = ((_a = data === null || data === void 0 ? void 0 : data.fileSystemDocument) === null || _a === void 0 ? void 0 : _a.type) === DocumentType.Composed;
    const initialValues = { email: '', forceConvertDocument: isComposed };
    const handleSubmit = (values) => __awaiter(this, void 0, void 0, function* () {
        const { email, forceConvertDocument } = values;
        try {
            yield createInvite(email, forceConvertDocument);
            addToast({ title: t `Invite sent`, appearance: 'success' });
            if (onSubmit) {
                onSubmit();
            }
            onClose();
        }
        catch (exception) {
            addToast({
                title: t({
                    message: 'There was a problem sending the invite',
                    comment: 'Generic error message in toast when inviting a external user to a document',
                }),
                appearance: 'danger',
            });
        }
    });
    const title = t({
        message: 'Share with people',
        comment: 'share document dialog title',
    });
    return (<FormDialog validateOnMount state={{ loading, error: Boolean(queryError) }} initialValues={initialValues} validationSchema={schema} onSubmit={handleSubmit} onClose={onClose} size="sm">
      <DialogHeader title={title} icon={<Falcon icon="share-square"/>}/>
      <DialogContent>
        <Box flexDirection="column" gap="lg">
          <Typography>
            <Trans comment="Share document dialog - Descriptive information message">
              Share document outside of organization. Recipients can leave and respond to comments but are not able to
              edit the document.
            </Trans>
          </Typography>
          <Box flexDirection="column">
            <TextField stretch name="email" label={t({ message: 'Email', comment: 'Share document dialog email input label' })} placeholder={t({
            message: 'Enter email...',
            comment: 'Share document dialog email input placeholder',
        })}/>
          </Box>
          {isComposed && (<Alert appearance="warning" message={t({
                comment: 'Share document dialog - Convert document to free-text info',
                message: 'If you want to share this document it will be converted to a free-text document. This means that you cannot go back and edit it in composed mode later.',
            })}/>)}
          <ErrorMessage error={error}/>
        </Box>
      </DialogContent>
      <DialogFooter>
        <CancelButton size="small" onClick={onClose}>
          <Trans comment="Share document dialog - Cancel button label">Cancel</Trans>
        </CancelButton>
        <SubmitButton size="small">
          {isComposed ? (<Trans comment="Submit text to share composed document">Convert & Send</Trans>) : (<Trans comment="Submit text to share document">Send</Trans>)}
        </SubmitButton>
      </DialogFooter>
    </FormDialog>);
}
